/**
 * PropertyFinder - Core Reusable Property Finder Component
 * 
 * This is a SCORABLE component - theme-agnostic and reusable across all themes.
 * Provides a split view with map on one side and property listings on the other.
 * Uses Tailwind CSS for styling - themes can override via className prop.
 */

'use client';

import type { Property } from '@/lib/api/clients/properties';
import { useState } from 'react';
import { PropertyCard } from './PropertyCard';

export interface PropertyFinderProps {
  properties: Property[];
  organisationId: string;
  locale?: string;
  className?: string;
  /**
   * Custom map component renderer
   * Themes can pass their own map implementation
   */
  renderMap?: (props: MapRendererProps) => React.ReactNode;
  /**
   * Layout variant
   */
  layout?: 'split' | 'side-by-side' | 'list-only';
  /**
   * Map marker click handler
   */
  onMarkerClick?: (property: Property) => void;
  /**
   * Empty state message
   */
  emptyMessage?: string;
}

export interface MapRendererProps {
  properties: Property[];
  selectedPropertyId: string | null;
  onMarkerClick: (property: Property) => void;
  className?: string;
}

/**
 * Core Property Finder Component
 * 
 * Provides the semantic structure and data display logic.
 * Themes can customize appearance via className overrides.
 */
export function PropertyFinder({
  properties,
  organisationId,
  locale = 'en',
  className = '',
  renderMap,
  layout = 'split',
  onMarkerClick,
  emptyMessage = 'No properties available',
}: PropertyFinderProps) {
  const [selectedPropertyId, setSelectedPropertyId] = useState<string | null>(
    properties.length > 0 ? properties[0]!.id : null
  );

  const handlePropertyClick = (property: Property) => {
    setSelectedPropertyId(property.id);
    onMarkerClick?.(property);
  };

  const handleMarkerClick = (property: Property) => {
    setSelectedPropertyId(property.id);
    onMarkerClick?.(property);
    // Scroll to property in list
    const element = document.getElementById(`property-${property.id}`);
    element?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  };

  if (properties.length === 0) {
    return (
      <div className={`flex items-center justify-center min-h-[600px] ${className}`}>
        <div className="text-center">
          <p className="text-lg text-gray-500">{emptyMessage}</p>
        </div>
      </div>
    );
  }

  // Determine layout classes
  const getLayoutClasses = () => {
    switch (layout) {
      case 'side-by-side':
        return 'flex flex-col lg:flex-row';
      case 'list-only':
        return 'flex flex-col';
      case 'split':
      default:
        return 'flex flex-col lg:flex-row';
    }
  };

  return (
    <div className={`${getLayoutClasses()} h-[calc(100vh-200px)] min-h-[600px] ${className}`}>
      {/* Map Section */}
      {layout !== 'list-only' && (
        <div className={`w-full ${
          layout === 'side-by-side' ? 'lg:w-1/2' : 'lg:w-1/2'
        } h-full`}>
          {renderMap ? (
            renderMap({
              properties,
              selectedPropertyId,
              onMarkerClick: handleMarkerClick,
              className: 'w-full h-full',
            })
          ) : (
            <DefaultMapRenderer
              properties={properties}
              selectedPropertyId={selectedPropertyId}
              onMarkerClick={handleMarkerClick}
            />
          )}
        </div>
      )}

      {/* Property List Section */}
      <div className={`w-full ${
        layout === 'side-by-side' ? 'lg:w-1/2' : 'lg:w-1/2'
      } overflow-y-auto`}>
        <div className="grid grid-cols-1 gap-4 p-4">
          {properties.map((property) => (
            <div
              key={property.id}
              id={`property-${property.id}`}
              onClick={() => handlePropertyClick(property)}
              className={`cursor-pointer transition-all ${
                selectedPropertyId === property.id
                  ? 'ring-2 ring-primary shadow-lg scale-[1.02]'
                  : 'hover:shadow-md'
              }`}
            >
              <PropertyCard
                property={property}
                href={`/${organisationId}/${locale}/properties/${property.id}`}
                variant="compact"
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * Default Map Renderer (Placeholder)
 * 
 * Shows a placeholder until themes provide their own map implementation.
 * Themes should integrate with their preferred map library (Google Maps, Mapbox, Leaflet, etc.)
 */
function DefaultMapRenderer({
  properties,
  selectedPropertyId,
  onMarkerClick,
}: MapRendererProps) {
  return (
    <div className="w-full h-full bg-gray-100 flex items-center justify-center relative">
      {/* Placeholder Map */}
      <div className="text-center p-8">
        <div className="text-6xl mb-4">🗺️</div>
        <h3 className="text-xl font-semibold text-gray-700 mb-2">
          Map View
        </h3>
        <p className="text-gray-600 text-sm mb-4">
          {properties.length} {properties.length === 1 ? 'property' : 'properties'} available
        </p>
        <p className="text-gray-500 text-xs">
          Integrate your preferred map library
        </p>
      </div>

      {/* Mock Property Markers */}
      <div className="absolute inset-0 pointer-events-none">
        {properties.map((property, index) => {
          // Random positions for demonstration
          const left = 20 + (index * 15) % 70;
          const top = 20 + (index * 20) % 60;
          
          return (
            <div
              key={property.id}
              className={`absolute w-8 h-8 rounded-full border-2 transition-all ${
                selectedPropertyId === property.id
                  ? 'bg-red-500 border-white scale-125 shadow-lg'
                  : 'bg-blue-500 border-white'
              }`}
              style={{ left: `${left}%`, top: `${top}%` }}
              onClick={(e) => {
                e.stopPropagation();
                onMarkerClick(property);
              }}
            >
              <span className="flex items-center justify-center h-full text-white text-xs font-bold">
                {index + 1}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

